import { apiFetch } from '@/lib/api/client.js';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Wire-layer Zod schemas (snake_case — match backend response exactly)
// ---------------------------------------------------------------------------

const OwnerWireSchema = z.object({
  id: z.string(),
  display_name: z.string(),
});

const SavedViewSummaryWireSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: z.enum(['personal', 'shared']),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: OwnerWireSchema.nullable().optional(),
  // Note: backend also returns `owner` in some shapes — accept both
  owner: OwnerWireSchema.nullable().optional(),
});

const SavedViewStateWireSchema = z.object({
  params: z.record(z.unknown()),
  view: z.record(z.unknown()).optional(),
});

const SavedViewWireSchema = SavedViewSummaryWireSchema.extend({
  state: z.union([SavedViewStateWireSchema, z.record(z.unknown())]),
});

const ListSavedViewsResponseWireSchema = z.object({
  items: z.array(SavedViewWireSchema),
  total: z.number().optional(),
});

// ---------------------------------------------------------------------------
// Domain types (camelCase)
// ---------------------------------------------------------------------------

export interface SavedViewOwner {
  readonly id: string;
  readonly displayName: string;
}

export interface SavedViewSummary {
  readonly id: string;
  readonly name: string;
  readonly scope: 'personal' | 'shared';
  readonly owner: SavedViewOwner | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SavedViewState {
  readonly params: Record<string, unknown>;
  readonly view: Record<string, unknown>;
}

export interface SavedView extends SavedViewSummary {
  readonly state: SavedViewState;
}

export interface SavedViewsListResult {
  readonly items: SavedView[];
  readonly total: number;
}

// Body for create / update (replace semantics for PUT)
export interface SavedViewBody {
  name: string;
  scope: 'personal' | 'shared';
  state: {
    params: Record<string, unknown>;
    view: Record<string, unknown>;
  };
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

function resolveOwner(wire: z.infer<typeof SavedViewSummaryWireSchema>): SavedViewOwner | null {
  const raw = wire.owner ?? wire.created_by ?? null;
  if (raw === null || raw === undefined) return null;
  return { id: raw.id, displayName: raw.display_name };
}

function mapWireView(wire: z.infer<typeof SavedViewWireSchema>): SavedView {
  const rawState = wire.state as Record<string, unknown>;
  const params = (rawState.params as Record<string, unknown> | undefined) ?? {};
  const view = (rawState.view as Record<string, unknown> | undefined) ?? {};

  return {
    id: wire.id,
    name: wire.name,
    scope: wire.scope,
    owner: resolveOwner(wire),
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
    state: { params, view },
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function listSavedViews(tenantReportId: string): Promise<SavedViewsListResult> {
  const raw = await apiFetch<unknown>(`/reports/${tenantReportId}/views`);
  const parsed = ListSavedViewsResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Saved views list response shape mismatch: ${parsed.error.message}`);
  }
  const items = parsed.data.items.map(mapWireView);
  return { items, total: parsed.data.total ?? items.length };
}

export async function createSavedView(
  tenantReportId: string,
  body: SavedViewBody,
): Promise<SavedView> {
  const raw = await apiFetch<unknown>(`/reports/${tenantReportId}/views`, {
    method: 'POST',
    body: JSON.stringify({
      name: body.name,
      scope: body.scope,
      state: body.state,
    }),
  });
  const parsed = SavedViewWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Create saved view response shape mismatch: ${parsed.error.message}`);
  }
  return mapWireView(parsed.data);
}

export async function updateSavedView(viewId: string, body: SavedViewBody): Promise<SavedView> {
  const raw = await apiFetch<unknown>(`/reports/views/${viewId}`, {
    method: 'PUT',
    body: JSON.stringify({
      name: body.name,
      scope: body.scope,
      state: body.state,
    }),
  });
  const parsed = SavedViewWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Update saved view response shape mismatch: ${parsed.error.message}`);
  }
  return mapWireView(parsed.data);
}

export async function deleteSavedView(viewId: string): Promise<void> {
  await apiFetch<undefined>(`/reports/views/${viewId}`, { method: 'DELETE' });
}
